import * as XLSX from "xlsx";

export const emptyRankingRow = () => ({
  team_id: "",
  position: "",
  total_time: "",
  total_penalty: "",
  points: "",
});

export const initialRankingForm = {
  category: "",
  after_stage: "",
  rankings: [emptyRankingRow()],
};

export const RANKING_EXPORT_HEADERS = [
  "position",
  "team_id",
  "team_number",
  "team_name",
  "total_time",
  "total_penalty",
  "points",
];

const HEADER_ALIASES = {
  position: ["position", "pos", "rank"],
  team_id: ["team_id", "teamid", "team id", "id"],
  team_number: [
    "team_number",
    "teamnumber",
    "team number",
    "team #",
    "team#",
    "number",
  ],
  team_name: ["team_name", "teamname", "team name", "team", "name"],
  total_time: ["total_time", "totaltime", "total time", "time"],
  total_penalty: [
    "total_penalty",
    "totalpenalty",
    "total penalty",
    "penalty",
  ],
  points: ["points", "point", "pts", "score"],
};

export const normalizeRankings = (data) => {
  if (Array.isArray(data)) return data;
  return (
    data?.data ||
    data?.rankings ||
    data?.items ||
    data?.results ||
    []
  );
};

export const normalizeStages = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.stages || data?.items || [];
};

export const normalizeCompetitors = (data) => {
  if (Array.isArray(data)) return data;
  return (
    data?.data ||
    data?.competitors ||
    data?.teams ||
    data?.items ||
    []
  );
};

export const getStageNumber = (stage) => {
  if (stage == null) return "";
  if (typeof stage === "number") return stage;
  const value =
    stage.stage_number ??
    stage.stageNumber ??
    stage.number ??
    stage.after_stage;
  if (value === 0 || value) return Number(value);
  return "";
};

export const getStageLabel = (stage) => {
  const number = getStageNumber(stage);
  const name = stage?.stage_name || stage?.name || stage?.title || "";
  if (number === 0 || number) {
    return name ? `Stage ${number} — ${name}` : `Stage ${number}`;
  }
  return name || "Stage";
};

export const getCompetitorId = (competitor) => {
  if (!competitor) return "";
  return (
    competitor._id ||
    competitor.id ||
    competitor.team_id ||
    competitor.teamId ||
    ""
  );
};

export const getCompetitorNumber = (competitor) => {
  if (!competitor) return "";
  const number =
    competitor.team_number ?? competitor.teamNumber ?? competitor.number;
  if (number === 0 || number) return String(number);
  return "";
};

export const getCompetitorName = (competitor) => {
  if (!competitor) return "";
  return (
    competitor.team_name ||
    competitor.teamName ||
    competitor.name ||
    "Unnamed team"
  );
};

export const getCompetitorLabel = (competitor) => {
  if (!competitor) return "";
  const teamName = getCompetitorName(competitor);
  const teamNumber = getCompetitorNumber(competitor);
  const numberPart = teamNumber ? ` #${teamNumber}` : "";
  return `${teamName}${numberPart}`;
};

export const getRankingTeam = (entry) => {
  const team = entry?.team_id;
  if (team && typeof team === "object") return team;
  return null;
};

export const getRankingTeamName = (entry) => {
  const team = getRankingTeam(entry);
  if (team) {
    return team.team_name || team.teamName || team.name || "-";
  }
  return entry?.team_name || "-";
};

export const getRankingTeamNumber = (entry) => {
  const team = getRankingTeam(entry);
  if (team) {
    const number = team.team_number ?? team.teamNumber ?? team.number;
    if (number === 0 || number) return String(number);
  }
  const fallback = entry?.team_number ?? entry?.teamNumber;
  if (fallback === 0 || fallback) return String(fallback);
  return "-";
};

export const getRankingDriverName = (entry) => {
  const team = getRankingTeam(entry);
  const driver = team?.driver_id || team?.driver || entry?.driver_id;
  if (driver && typeof driver === "object") {
    return driver.name || driver.full_name || driver.fullName || "-";
  }
  if (typeof driver === "string" && driver) return driver;
  return entry?.driver_name || "-";
};

export const getRankingCategoryLabel = (entry, categories = []) => {
  const key =
    typeof entry?.category === "string"
      ? entry.category
      : entry?.category?.key || entry?.category?.title || "";
  if (!key) return "-";
  const match = categories.find(
    (category) =>
      category?.key === key ||
      category?._id === key ||
      category?.id === key
  );
  if (match) return match.title || match.key || key;
  if (entry?.category && typeof entry.category === "object") {
    return entry.category.title || entry.category.key || key;
  }
  return key;
};

export const getRankingApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const validateRankingForm = (form) => {
  const errors = {};

  if (!form.category?.trim()) {
    errors.category = "Category is required.";
  }

  const afterStage = Number(form.after_stage);
  if (
    form.after_stage === "" ||
    Number.isNaN(afterStage) ||
    !Number.isFinite(afterStage) ||
    afterStage < 0 ||
    !Number.isInteger(afterStage)
  ) {
    errors.after_stage = "After stage is required.";
  }

  const rankings = Array.isArray(form.rankings) ? form.rankings : [];
  if (!rankings.length) {
    errors.rankings = "Add at least one ranking row.";
  }

  const rowErrors = {};
  const seenTeams = new Set();

  rankings.forEach((row, index) => {
    const rowError = {};
    const teamId = String(row?.team_id || "").trim();
    if (!teamId) {
      rowError.team_id = "Team is required.";
    } else if (seenTeams.has(teamId)) {
      rowError.team_id = "Duplicate team in this snapshot.";
    } else {
      seenTeams.add(teamId);
    }

    const position = Number(row?.position);
    if (
      row?.position === "" ||
      Number.isNaN(position) ||
      !Number.isFinite(position) ||
      position < 1 ||
      !Number.isInteger(position)
    ) {
      rowError.position = "Position must be a positive integer.";
    }

    if (row?.points !== "" && row?.points != null) {
      const points = Number(row.points);
      if (
        Number.isNaN(points) ||
        !Number.isFinite(points) ||
        points < 0 ||
        points > 100
      ) {
        rowError.points = "Points must be between 0 and 100.";
      }
    }

    if (Object.keys(rowError).length) {
      rowErrors[index] = rowError;
    }
  });

  if (Object.keys(rowErrors).length) {
    errors.rows = rowErrors;
  }

  return errors;
};

export const buildRankingPayload = (form) => {
  const rankings = (form.rankings || []).map((row) => {
    const item = {
      team_id: String(row.team_id).trim(),
      position: Number(row.position),
    };

    const totalTime = String(row.total_time || "").trim();
    if (totalTime) item.total_time = totalTime;

    const totalPenalty = String(row.total_penalty || "").trim();
    if (totalPenalty) item.total_penalty = totalPenalty;

    if (row.points !== "" && row.points != null) {
      item.points = Number(row.points);
    }

    return item;
  });

  return {
    category: String(form.category).trim(),
    after_stage: Number(form.after_stage),
    rankings,
  };
};

const normalizeHeaderKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const resolveHeaderField = (header) => {
  const normalized = normalizeHeaderKey(header);
  return (
    Object.keys(HEADER_ALIASES).find((field) =>
      HEADER_ALIASES[field].some(
        (alias) => normalizeHeaderKey(alias) === normalized
      )
    ) || null
  );
};

const findCompetitorById = (competitors, teamId) =>
  competitors.find((item) => getCompetitorId(item) === String(teamId || ""));

const resolveImportedTeamId = (row, competitors = []) => {
  const teamId = String(row.team_id || "").trim();
  if (teamId) {
    const byId = findCompetitorById(competitors, teamId);
    if (byId) return getCompetitorId(byId);
    return teamId;
  }

  const teamNumber = String(row.team_number ?? "").trim();
  if (teamNumber) {
    const byNumber = competitors.find(
      (item) => getCompetitorNumber(item) === teamNumber
    );
    if (byNumber) return getCompetitorId(byNumber);
  }

  const teamName = String(row.team_name || "")
    .trim()
    .toLowerCase();
  if (teamName) {
    const byName = competitors.find(
      (item) => getCompetitorName(item).toLowerCase() === teamName
    );
    if (byName) return getCompetitorId(byName);
  }

  return "";
};

export const buildRankingSheetRows = (rankings = [], competitors = []) => {
  return (rankings || []).map((row) => {
    const competitor = findCompetitorById(competitors, row.team_id);
    return {
      position: row.position ?? "",
      team_id: row.team_id || "",
      team_number: competitor
        ? getCompetitorNumber(competitor)
        : row.team_number || "",
      team_name: competitor
        ? getCompetitorName(competitor)
        : row.team_name || "",
      total_time: row.total_time || "",
      total_penalty: row.total_penalty || "",
      points: row.points ?? "",
    };
  });
};

export const buildSampleRankingRows = (competitors = []) => {
  if (!competitors.length) {
    return [
      {
        position: 1,
        team_id: "",
        team_number: "",
        team_name: "",
        total_time: "49H 00' 41'",
        total_penalty: "00H 00' 00'",
        points: 25,
      },
    ];
  }

  return competitors.map((competitor, index) => ({
    position: index + 1,
    team_id: getCompetitorId(competitor),
    team_number: getCompetitorNumber(competitor),
    team_name: getCompetitorName(competitor),
    total_time: "",
    total_penalty: "",
    points: "",
  }));
};

export const downloadRankingsWorkbook = ({
  rows = [],
  filename = "rankings",
  format = "xlsx",
}) => {
  const sheetRows = rows.map((row) =>
    RANKING_EXPORT_HEADERS.map((header) => row[header] ?? "")
  );
  const worksheet = XLSX.utils.aoa_to_sheet([
    RANKING_EXPORT_HEADERS,
    ...sheetRows,
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Rankings");

  if (format === "csv") {
    XLSX.writeFile(workbook, `${filename}.csv`, { bookType: "csv" });
    return;
  }

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

const mapSheetRowsToObjects = (sheetRows = []) => {
  if (!sheetRows.length) return [];

  const headerRow = sheetRows[0] || [];
  const fieldIndexes = {};
  headerRow.forEach((header, index) => {
    const field = resolveHeaderField(header);
    if (field && fieldIndexes[field] == null) {
      fieldIndexes[field] = index;
    }
  });

  return sheetRows.slice(1).map((row) => {
    const item = {};
    Object.keys(HEADER_ALIASES).forEach((field) => {
      const index = fieldIndexes[field];
      item[field] =
        index == null || row[index] == null ? "" : String(row[index]).trim();
    });
    return item;
  });
};

export const parseRankingsImportFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          reject(new Error("The file has no sheets."));
          return;
        }
        const worksheet = workbook.Sheets[firstSheetName];
        const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: false,
        });
        resolve(mapSheetRowsToObjects(sheetRows));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read the file."));
    reader.readAsArrayBuffer(file);
  });

export const mapImportedRowsToRankings = (rows = [], competitors = []) => {
  const warnings = [];
  const rankings = [];

  rows.forEach((row, index) => {
    const isBlank =
      !String(row.position || "").trim() &&
      !String(row.team_id || "").trim() &&
      !String(row.team_number || "").trim() &&
      !String(row.team_name || "").trim() &&
      !String(row.total_time || "").trim() &&
      !String(row.total_penalty || "").trim() &&
      !String(row.points || "").trim();

    if (isBlank) return;

    const teamId = resolveImportedTeamId(row, competitors);
    if (!teamId) {
      warnings.push(
        `Row ${index + 2}: could not match a team (provide team_id, team_number, or team_name).`
      );
    }

    rankings.push({
      team_id: teamId,
      position: String(row.position || "").trim(),
      total_time: String(row.total_time || "").trim(),
      total_penalty: String(row.total_penalty || "").trim(),
      points: String(row.points || "").trim(),
    });
  });

  return { rankings, warnings };
};
